import { Platform } from 'react-native'
import * as DocumentPicker from 'expo-document-picker'
import * as ImagePicker from 'expo-image-picker'

/**
 * Some Android devices ship a broken or incomplete implementation of the
 * modern system Photo Picker. ACTION_OPEN_DOCUMENT uses the older, broadly
 * supported DocumentsUI surface instead. Copying the selection into our cache
 * also gives upload and QR-scanning code a stable app-local URI.
 */
export async function launchMediaLibraryAsync(
  options: ImagePicker.ImagePickerOptions
): Promise<ImagePicker.ImagePickerResult> {
  if (Platform.OS !== 'android') return ImagePicker.launchImageLibraryAsync(options)

  const result = await DocumentPicker.getDocumentAsync({
    type: getMimeTypes(options.mediaTypes),
    multiple: options.allowsMultipleSelection,
    copyToCacheDirectory: true
  })

  if (result.canceled) return { canceled: true, assets: null }

  const requestedMimeTypes = getMimeTypes(options.mediaTypes)

  return {
    canceled: false,
    assets: result.assets.map((asset) => {
      const mimeType = normalizeMimeType(asset.mimeType)
      return {
        uri: asset.uri,
        width: 0,
        height: 0,
        fileName: asset.name,
        fileSize: asset.size,
        mimeType,
        type: mimeType?.startsWith('video/') || requestedMimeTypes === 'video/*' ? 'video' : 'image'
      }
    })
  }
}

function normalizeMimeType(mimeType?: string) {
  const normalized = mimeType?.split(';', 1)[0].trim().toLowerCase()
  return normalized === 'application/octet-stream' ? undefined : normalized
}

function getMimeTypes(mediaTypes: ImagePicker.ImagePickerOptions['mediaTypes']) {
  const requestedTypes = (Array.isArray(mediaTypes) ? mediaTypes : [mediaTypes ?? 'images']).map((type) =>
    String(type).toLowerCase()
  )
  const allowsImages = requestedTypes.some((type) => type === 'images' || type === 'all')
  const allowsVideos = requestedTypes.some((type) => type === 'videos' || type === 'all')

  if (allowsImages && allowsVideos) return ['image/*', 'video/*']
  if (allowsVideos) return 'video/*'
  return 'image/*'
}
